import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useSyncExternalStore,
} from "react";

const initialState = {
	taskInfo: {},
	current: "Details",
	taskError: {},
	stageName: "",
	stageList: [],
	hasImages: false,
	hasTools: false,
	hasParts: false,
	hasDocuments: false,
	hasSafetyCritical: false,
	hasQuestions: false,
	hasPermits: false,
};

function reducer(state, action) {
	const { payload, type } = action;
	switch (type) {
		case "SET_TASK_DETAIL":
			return {
				...state,
				taskInfo: payload,
			};

		case "TAB_COUNT":
			return {
				...state,
				taskInfo: { ...state.taskInfo, [payload.countTab]: payload.data },
			};

		case "CURRENT":
			return {
				...state,
				current: payload,
			};

		case "SET_TASK_ERROR":
			return {
				...state,
				taskError: { ...state.taskError, [payload.name]: payload.value },
			};
		case "TOGGLE_CUSTOM_INTERVALS":
			return {
				...state,
				taskInfo: {
					...state.taskInfo,
					customIntervals: !state.taskInfo.customIntervals,
				},
			};
		case "SET_STAGE_NAME":
			return { ...state, stageName: payload };

		case "SET_STAGE_LIST":
			return { ...state, stageList: payload };

		case "SET_TOOLS":
			return {
				...state,
				hasTools: payload,
			};
		case "SET_IMAGES":
			return {
				...state,
				hasImages: payload,
			};
		case "SET_QUESTIONS":
			return {
				...state,
				hasQuestions: payload,
			};
		case "SET_PERMITS":
			return {
				...state,
				hasPermits: payload,
			};
		case "SET_SAFETY":
			return {
				...state,
				hasSafetyCritical: payload,
			};
		case "SET_DOCUMENTS":
			return {
				...state,
				hasDocuments: payload,
			};
		case "SET_PARTS":
			return {
				...state,
				hasParts: payload,
			};

		default:
			return state;
	}
}

export const TaskContext = createContext();

const TaskDetailStoreContext = createContext(null);

// Caps how many distinct tasks' detail this store holds onto at once. A row's
// state has to survive its own unmount (that's the entire point of this
// store), so we can't evict on "listener count hit zero" - that's the normal,
// expected state of every scrolled-away row we still want to remember. What
// we bound instead is total distinct tasks touched in one long session on a
// very large task list: once over the cap, the least-recently-touched entries
// with no one currently subscribed (never a task still on screen) are dropped.
export const MAX_CACHED_TASKS = 200;

const createTaskDetailStore = () => {
	const states = new Map();
	const listeners = new Map();

	const pruneToCap = () => {
		if (states.size <= MAX_CACHED_TASKS) return;
		for (const taskId of states.keys()) {
			if (states.size <= MAX_CACHED_TASKS) break;
			if (!listeners.get(taskId)?.size) states.delete(taskId);
		}
	};

	return {
		getState: (taskId) => states.get(taskId) ?? initialState,
		subscribe: (taskId, listener) => {
			const set = listeners.get(taskId) ?? new Set();
			set.add(listener);
			listeners.set(taskId, set);
			return () => {
				set.delete(listener);
				if (set.size === 0) listeners.delete(taskId);
			};
		},
		dispatch: (taskId, action) => {
			const previous = states.get(taskId) ?? initialState;
			const next = reducer(previous, action);
			if (next === previous) return;
			// Re-inserting moves this key to the end of the Map's iteration
			// order, which is what makes the front of the Map "least recently
			// touched" for pruneToCap to walk.
			states.delete(taskId);
			states.set(taskId, next);
			pruneToCap();
			listeners.get(taskId)?.forEach((listener) => listener());
		},
	};
};

// Keyed by task ID so a task's detail survives the virtualizer tearing its
// row down and rebuilding it on scroll, instead of resetting to initialState
// and losing corrections a tab had already fetched.
export const TaskDetailStoreProvider = ({ children }) => {
	const storeRef = useRef(null);
	if (!storeRef.current) storeRef.current = createTaskDetailStore();

	return (
		<TaskDetailStoreContext.Provider value={storeRef.current}>
			{children}
		</TaskDetailStoreContext.Provider>
	);
};

const TaskDetailContext = ({ taskId, children }) => {
	const store = useContext(TaskDetailStoreContext);
	if (!store) {
		throw new Error(
			"TaskDetailContext must be used within a TaskDetailStoreProvider"
		);
	}

	const subscribe = useCallback(
		(listener) => store.subscribe(taskId, listener),
		[store, taskId]
	);
	const getSnapshot = useCallback(() => store.getState(taskId), [store, taskId]);
	const state = useSyncExternalStore(subscribe, getSnapshot);
	const dispatch = useCallback((action) => store.dispatch(taskId, action), [store, taskId]);
	const value = useMemo(() => [state, dispatch], [state, dispatch]);

	return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};

export default TaskDetailContext;
